import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';

@Component({
	selector: 'app-qoh-popover-panel',
	standalone: true,
	imports: [CommonModule],
	template: `
		<div
			#popoverRoot
			class="qoh-popover"
			[ngStyle]="style"
			[style.--qoh-cols]="gridLayout.columnCount"
			[style.--qoh-rows]="gridLayout.rowsPerColumn"
			[style.--qoh-mw.px]="gridLayout.popoverMaxWidthPx"
			role="region"
			[attr.id]="regionId"
			[attr.aria-labelledby]="showTotal ? labelledById : null"
			[attr.aria-label]="regionAriaLabel"
			tabindex="-1"
		>
			<ng-container *ngIf="rows.length">
				<h2 *ngIf="showTotal" class="qoh-popover__title" [attr.id]="labelledById">QOH by location</h2>
				<div class="qoh-popover__grid">
					<div
						*ngFor="let row of rows"
						class="qoh-popover__row"
						[attr.aria-label]="row.name + ', quantity ' + row.qty"
					>
						<span class="qoh-popover-pill qoh-popover-pill--location" aria-hidden="true">{{ row.name }}</span>
						<span
							class="qoh-popover-pill qoh-popover-pill--qty"
							[ngClass]="getQohPillClass(row.qty)"
							aria-hidden="true"
						>{{ row.qty }}</span>
					</div>
				</div>
				<div *ngIf="showTotal" class="qoh-popover__row qoh-popover__total" [attr.aria-label]="'Total, quantity ' + total">
					<span class="qoh-popover-pill qoh-popover-pill--location" aria-hidden="true">Total</span>
					<span class="qoh-popover-pill qoh-popover-pill--qty qoh-popover-pill--green" aria-hidden="true">{{ total }}</span>
				</div>
			</ng-container>
			<div *ngIf="!rows.length && total != null" class="qoh-popover__fallback">
				<span class="qoh-popover-pill qoh-popover-pill--qty qoh-popover-pill--green">QOH {{ total }}</span>
			</div>
		</div>
	`,
	styleUrls: ['./qoh-popover-panel.component.scss']
})
export class QohPopoverPanelComponent {
	private static readonly MAX_ROWS_PER_COLUMN = 4;

	@Input() item: { qoh?: any[]; canviewqoh?: number; locqoh?: number } | null = null;
	@Input() regionId = '';
	@Input() labelledById = '';

	@ViewChild('popoverRoot', { read: ElementRef }) popoverRootRef?: ElementRef<HTMLElement>;

	constructor(private hostRef: ElementRef<HTMLElement>) {}

	private getPositionedElement(): HTMLElement {
		const fromRef = this.popoverRootRef?.nativeElement;
		if (fromRef) return fromRef;
		const inner = this.hostRef.nativeElement?.querySelector?.('.qoh-popover') as HTMLElement | null;
		return inner ?? this.hostRef.nativeElement;
	}

	style: { [key: string]: string } = { visibility: 'hidden' };

	get total(): number {
		if (!this.item) return 0;
		return this.item.canviewqoh != null ? this.item.canviewqoh : (this.item.locqoh ?? 0);
	}

	get rows(): { name: string; qty: number }[] {
		if (!this.item?.qoh || !Array.isArray(this.item.qoh)) return [];
		return this.item.qoh.map((loc: any) => ({
			name: (loc.locationname || loc.loccode || loc.code || '?').trim(),
			qty: loc.quantity != null ? loc.quantity : 0
		}));
	}

	get showTotal(): boolean {
		return this.rows.length > 1;
	}

	get regionAriaLabel(): string | null {
		if (this.showTotal) {
			return null;
		}
		if (this.rows.length === 1) {
			return `${this.rows[0].name}, quantity ${this.rows[0].qty}`;
		}
		if (!this.rows.length && this.total != null) {
			return `Quantity on hand ${this.total}`;
		}
		return null;
	}

	get gridLayout(): {
		columnCount: number;
		rowsPerColumn: number;
		popoverMaxWidthPx: number;
	} {
		return this.computeGridLayout();
	}

	private computeGridLayout(): {
		columnCount: number;
		rowsPerColumn: number;
		popoverMaxWidthPx: number;
	} {
		const n = this.rows.length;
		const win = typeof globalThis !== 'undefined' && globalThis.window ? globalThis.window : null;
		const vw = win ? win.innerWidth : 1280;
		const vh = win ? win.innerHeight : 800;

		const margin = vw < 576 ? 10 : vw < 992 ? 14 : 18;
		const minColPx = vw < 400 ? 86 : vw < 576 ? 96 : vw < 768 ? 106 : 118;
		const maxColumnsViewport = Math.max(1, Math.min(14, Math.floor((vw - margin * 2) / minColPx)));
		const maxRowsPerCol = QohPopoverPanelComponent.MAX_ROWS_PER_COLUMN;

		const titleBlockPx = this.showTotal ? 56 : 34;
		const totalFooterPx = this.showTotal ? 40 : 0;
		const verticalChromePx = vw < 576 ? 18 : 22;
		const rowBudgetScale = vw < 576 ? 0.9 : 1;
		const budgetForRowsPx = Math.max(
			110,
			Math.min(520, Math.floor(vh * (vw < 576 ? 0.58 : 0.62)) * rowBudgetScale - titleBlockPx - totalFooterPx - verticalChromePx)
		);
		const estRowPx = vw < 576 ? 20 : 22;

		let columnCount = 1;
		let rowsPerColumn = 1;
		if (n > 0) {
			const idealColumnsForCap = Math.ceil(n / maxRowsPerCol);
			columnCount = Math.min(idealColumnsForCap, maxColumnsViewport, n);
			columnCount = Math.max(columnCount, 1);
			rowsPerColumn = Math.ceil(n / columnCount);
			const colCap = Math.min(maxColumnsViewport, n);
			while (rowsPerColumn * estRowPx > budgetForRowsPx && columnCount < colCap) {
				columnCount += 1;
				rowsPerColumn = Math.ceil(n / columnCount);
			}
		}

		const popoverMaxWidthPx = Math.min(vw - margin * 2, margin + columnCount * (minColPx + 4));
		return {
			columnCount,
			rowsPerColumn,
			popoverMaxWidthPx
		};
	}

	getQohPillClass(qty: number): string {
		const n = qty != null ? Number(qty) : 0;
		if (n <= 0) return 'qoh-popover-pill--red';
		if (n < 4) return 'qoh-popover-pill--yellow';
		return 'qoh-popover-pill--green';
	}

	updatePosition(rect: DOMRect) {
		const gap = Math.max(6, Math.round(Math.min(12, rect.height * 0.15)));
		const minSpaceAbove = Math.min(200, Math.max(140, (globalThis.window?.innerHeight ?? 600) * 0.28));
		const openAbove = rect.top >= minSpaceAbove;
		if (openAbove) {
			this.style = {
				left: `${rect.left}px`,
				top: `${rect.top - gap}px`,
				transform: 'translate(0, -100%)',
				visibility: 'visible'
			};
		} else {
			this.style = {
				left: `${rect.left}px`,
				top: `${rect.bottom + gap}px`,
				transform: 'none',
				visibility: 'visible'
			};
		}
	}

	clampToViewport() {
		const el = this.getPositionedElement();
		const box = el.getBoundingClientRect();
		const vw = globalThis.window?.innerWidth ?? 1024;
		const vh = globalThis.window?.innerHeight ?? 768;
		const pad = vw < 576 ? 6 : 8;
		let left = box.left;
		let top = box.top;
		if (box.right > vw - pad) {
			left -= box.right - (vw - pad);
		}
		if (left < pad) {
			left = pad;
		}
		if (box.bottom > vh - pad) {
			top -= box.bottom - (vh - pad);
		}
		if (top < pad) {
			top = pad;
		}
		this.style = {
			...this.style,
			left: `${left}px`,
			top: `${top}px`,
			transform: 'none',
			visibility: 'visible'
		};
	}
}
